import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest } from "../../api/client";
import { buildBasketTotalsFromApiPrices } from "../../utils/pasteListFlow";

function normalizeFeaturesFilterKey(value) {
  if (Array.isArray(value)) {
    return value
      .map((x) => String(x || "").trim())
      .filter(Boolean)
      .join(",")
      .toLowerCase();
  }
  return String(value || "").trim().toLowerCase();
}

export function getFeaturesRequestKey({
  query_list,
  category,
  sub_category,
  offer,
  start = 0,
  offset = 10,
} = {}) {
  const queryKey = String(query_list || "").trim().toLowerCase();
  const catKey = normalizeFeaturesFilterKey(category);
  const subKey = normalizeFeaturesFilterKey(sub_category);
  const offerKey = offer ? "1" : "0";
  return `features_v2:${encodeURIComponent(queryKey)}:${encodeURIComponent(catKey)}:${encodeURIComponent(
    subKey
  )}:${offerKey}:${start}:${offset}`;
}

function normalizePlatformKey(platform) {
  const p = String(platform || "").trim().toLowerCase();
  if (p === "morrisons") return "morrison";
  if (p === "sainsburry") return "sainsbury";
  if (p === "sainsbury" || p === "sainsburys") return "sainsbury";
  if (p === "asda" || p === "tesco" || p === "morrison" || p === "sainsbury") {
    return p;
  }
  return "";
}

function toActowizPlatformQueryParam(platform) {
  const key = normalizePlatformKey(platform);
  if (!key) return "";
  // Actowiz returns "sainsburry" as platform; filtered requests expect same.
  if (key === "sainsbury") return "sainsburry";
  return key;
}

function formatPlatformListForQuery(platform_list) {
  if (platform_list == null) return "";
  if (Array.isArray(platform_list)) {
    return platform_list
      .map((x) => toActowizPlatformQueryParam(x))
      .filter(Boolean)
      .join(",");
  }
  const raw = String(platform_list).trim();
  if (!raw) return "";
  return raw
    .split(",")
    .map((x) => toActowizPlatformQueryParam(x.trim()))
    .filter(Boolean)
    .join(",");
}

function normalizeListValues(value) {
  if (Array.isArray(value)) {
    return value.map((x) => String(x || "").trim()).filter(Boolean);
  }
  const raw = String(value || "").trim();
  return raw ? [raw] : [];
}

function appendListQueryParam(qs, key, value) {
  normalizeListValues(value).forEach((item) => qs.append(key, item));
}

function hasListFilterValue(value) {
  return normalizeListValues(value).length > 0;
}

function applySearchCatalogUpdate(state, { queryKey, totalCount, categories, hasFilters }) {
  const normalizedKey = String(queryKey || "").trim().toLowerCase();
  if (!normalizedKey) return;
  if (!Array.isArray(categories) || categories.length === 0) return;

  const existing = state.searchCatalog || { queryKey: null, totalCount: null, categories: null };
  const sameQuery = existing.queryKey === normalizedKey;
  const canUpdate =
    !hasFilters ||
    !sameQuery ||
    !Array.isArray(existing.categories) ||
    existing.categories.length === 0;

  if (canUpdate) {
    state.searchCatalog = {
      queryKey: normalizedKey,
      totalCount: typeof totalCount === "number" ? totalCount : existing.totalCount ?? null,
      categories,
    };
  } else if (sameQuery && typeof totalCount === "number") {
    state.searchCatalog.totalCount = totalCount;
  }
}

function canUpdateSharedAllCatalog(existing, { hasFilters, queryListKey }) {
  const hasExistingCategories =
    Boolean(existing) && Array.isArray(existing.categories) && existing.categories.length > 0;
  if (hasFilters && hasExistingCategories) return false;
  if (!String(queryListKey || "").trim() && hasExistingCategories) return false;
  return !hasFilters || !hasExistingCategories;
}

function firstOfferDescription(p) {
  const offers = p?.offers;
  if (!Array.isArray(offers) || offers.length === 0) return "";
  const x = offers[0];
  if (!x || typeof x !== "object") return "";
  return (
    x.description ||
    x.desc ||
    x.longDescription ||
    x.title ||
    x.text ||
    ""
  );
}

function getProviderOfferPrice(provider) {
  const loyaltyPrice = parsePrice(
    provider?.loyalty_pricing ?? provider?.loyaltyPricing ?? provider?.loyalty_price
  );
  if (Number.isFinite(loyaltyPrice)) return loyaltyPrice;

  const offers = Array.isArray(provider?.offers) ? provider.offers : [];
  const offerFromArray = parsePrice(
    offers[0]?.offer_price ?? offers[0]?.price ?? offers[0]?.unit_price
  );
  if (Number.isFinite(offerFromArray)) return offerFromArray;

  return null;
}

function getProviderPromotionText(provider) {
  const fromOffer = firstOfferDescription(provider);
  if (fromOffer) return String(fromOffer).trim();
  return String(
    provider?.promotion ||
      provider?.promotional_text ||
      provider?.offer_text ||
      provider?.promo_text ||
      provider?.multi_buy ||
      provider?.deal_text ||
      ""
  ).trim();
}

function extractComparisonEntry(provider) {
  const platform = normalizePlatformKey(provider?.platform);
  if (!platform) return null;

  const listedPrice = parsePrice(provider?.price);
  const regularPrice = parsePrice(
    provider?.regular_price ??
      provider?.base_price ??
      provider?.original_price ??
      provider?.was_price
  );
  let offerPrice = getProviderOfferPrice(provider);
  const promotion = getProviderPromotionText(provider);

  if (!Number.isFinite(offerPrice) && promotion && Number.isFinite(listedPrice)) {
    offerPrice = listedPrice;
  }

  const basePrice = Number.isFinite(regularPrice)
    ? regularPrice
    : Number.isFinite(offerPrice) && Number.isFinite(listedPrice) && listedPrice !== offerPrice
      ? listedPrice
      : Number.isFinite(listedPrice) && !promotion
        ? listedPrice
        : Number.isFinite(listedPrice) && !Number.isFinite(offerPrice)
          ? listedPrice
          : null;
  const url =
    provider?.url ||
    provider?.product_url ||
    provider?.productUrl ||
    provider?.store_url ||
    provider?.storeUrl ||
    provider?.link ||
    null;
  const productName =
    String(
      provider?.product_name ??
        provider?.productName ??
        provider?.product_title ??
        provider?.title ??
        ""
    ).trim() || "";
  const matchType =
    String(
      provider?.match_type ??
        provider?.matchType ??
        provider?.response?.match_type ??
        provider?.response?.matchType ??
        ""
    ).trim() || "";
  const averageRatingRaw =
    provider?.average_rating ?? provider?.avg_rating ?? provider?.rating ?? provider?.review_rating;
  const averageRating = parsePrice(averageRatingRaw);
  const reviewCount = Number.parseInt(
    String(
      provider?.review_count ??
        provider?.reviews ??
        provider?.rating_count ??
        provider?.reviewCount ??
        ""
    ).replace(/[^0-9]/g, ""),
    10
  );
  const productId = String(
    provider?.product_id ?? provider?.pid ?? provider?.sku ?? ""
  ).trim();
  const brand = String(provider?.brand ?? "").trim();
  const description = String(provider?.description ?? "").trim();

  return {
    platform,
    product_id: productId,
    price: Number.isFinite(basePrice) ? basePrice : null,
    offer_price: Number.isFinite(offerPrice) ? offerPrice : null,
    promotion,
    url,
    average_rating: Number.isFinite(averageRating) ? averageRating : null,
    review_count: Number.isFinite(reviewCount) ? reviewCount : null,
    match_type: matchType,
    product_name: productName,
    brand,
    description,
  };
}

function isBetterComparisonEntry(candidate, prev) {
  if (!candidate) return false;
  if (!prev) return true;

  const candidateOffer = candidate?.offer_price;
  const prevOffer = prev?.offer_price;
  if (Number.isFinite(candidateOffer) && Number.isFinite(prevOffer)) {
    return candidateOffer < prevOffer;
  }
  if (Number.isFinite(candidateOffer) && !Number.isFinite(prevOffer)) return true;
  if (!Number.isFinite(candidateOffer) && Number.isFinite(prevOffer)) return false;

  const candidateBase = candidate?.price;
  const prevBase = prev?.price;
  if (Number.isFinite(candidateBase) && Number.isFinite(prevBase)) {
    return candidateBase < prevBase;
  }
  if (Number.isFinite(candidateBase) && !Number.isFinite(prevBase)) return true;
  if (!Number.isFinite(candidateBase) && Number.isFinite(prevBase)) return false;

  return false;
}

function normalizeProvider(p) {
  const images = Array.isArray(p?.all_images)
    ? p.all_images
    : p?.main_image_url
    ? [p.main_image_url]
    : [];
  const offerLine = firstOfferDescription(p);
  const offers = Array.isArray(p?.offers) ? p.offers : null;
  const response = p?.response && typeof p.response === "object" ? p.response : null;
  const matchTypeRaw =
    p?.match_type ?? p?.matchType ?? response?.match_type ?? response?.matchType ?? "";
  const match_type = String(matchTypeRaw || "").trim();
  const productNameRaw =
    p?.product_name ??
    p?.productName ??
    response?.product_name ??
    response?.productName ??
    "";
  const product_name = String(productNameRaw || "").trim();
  return {
    platform: normalizePlatformKey(p?.platform),
    pid: p?.product_id,
    sku: p?.sku || p?.product_id,
    scrape_time: p?.scrape_time || "",
    title: p?.product_name || p?.sku || "",
    price: p?.price != null ? String(p.price) : "",
    response,
    match_type,
    product_name,
    loyalty_pricing:
      p?.loyalty_pricing ??
      p?.loyaltyPricing ??
      p?.loyalty_price ??
      null,
    offers,
    packsize: p?.pack_size || "",
    category: p?.category || p?.category_name || p?.department || "",
    subcategory:
      p?.sub_category ||
      p?.subCategory ||
      p?.subcategory ||
      p?.sub_category_name ||
      p?.subcategory_name ||
      "",
    promotion:
      offerLine ||
      p?.promotion ||
      p?.promotional_text ||
      p?.offer_text ||
      p?.promo_text ||
      p?.multi_buy ||
      p?.deal_text ||
      "",
    updated_at:
      p?.updated_at ??
      p?.scraped_at ??
      p?.last_updated ??
      p?.modified_at ??
      p?.scrape_time ??
      (typeof p?.last_updated_time === "number" ? p.last_updated_time : null) ??
      null,
    images,
    description: p?.description || "",
    url:
      p?.url ||
      p?.product_url ||
      p?.productUrl ||
      p?.store_url ||
      p?.storeUrl ||
      p?.link ||
      null,
    average_rating: p?.average_rating,
    review_count: p?.review_count,
  };
}

function parsePrice(value) {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.\-]/g, "");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function buildComparison(providers) {
  const order = ["tesco", "morrison", "asda", "sainsbury"];
  const byPlatform = {};

  (Array.isArray(providers) ? providers : []).forEach((provider) => {
    const entry = extractComparisonEntry(provider);
    if (!entry?.platform) return;
    const prev = byPlatform[entry.platform];
    if (isBetterComparisonEntry(entry, prev)) {
      byPlatform[entry.platform] = entry;
      return;
    }
    if (!prev) {
      byPlatform[entry.platform] = entry;
      return;
    }
    if (!prev?.url && entry?.url) {
      byPlatform[entry.platform] = { ...prev, url: entry.url };
    } else if (prev?.average_rating == null && entry?.average_rating != null) {
      byPlatform[entry.platform] = { ...prev, average_rating: entry.average_rating };
    } else if (prev?.review_count == null && entry?.review_count != null) {
      byPlatform[entry.platform] = { ...prev, review_count: entry.review_count };
    }
  });

  return order.map((platform) => ({
    platform,
    product_id: byPlatform?.[platform]?.product_id || "",
    price: Number.isFinite(byPlatform?.[platform]?.price) ? byPlatform[platform].price : null,
    offer_price: Number.isFinite(byPlatform?.[platform]?.offer_price)
      ? byPlatform[platform].offer_price
      : null,
    promotion: byPlatform?.[platform]?.promotion || "",
    url: byPlatform?.[platform]?.url || null,
    average_rating: Number.isFinite(byPlatform?.[platform]?.average_rating)
      ? byPlatform[platform].average_rating
      : null,
    review_count: Number.isFinite(byPlatform?.[platform]?.review_count)
      ? byPlatform[platform].review_count
      : null,
    match_type: byPlatform?.[platform]?.match_type || "",
    product_name: byPlatform?.[platform]?.product_name || "",
    brand: byPlatform?.[platform]?.brand || "",
    description: byPlatform?.[platform]?.description || "",
  }));
}

function getSearchRequestKey(query, start = 0, offset = 10, limit = offset) {
  const q = String(query || "").trim().toLowerCase();
  return `search:${encodeURIComponent(q)}:${start}:${offset}:${limit}`;
}

function pickBestProvider(providers) {
  if (!Array.isArray(providers) || providers.length === 0) return null;
  let best = providers[0];
  let bestPrice = parsePrice(best?.price);

  for (const p of providers) {
    const pr = parsePrice(p?.price);
    if (Number.isFinite(pr) && (!Number.isFinite(bestPrice) || pr < bestPrice)) {
      best = p;
      bestPrice = pr;
    }
  }
  return best;
}

function mergeCategoryTrees(trees) {
  const map = new Map(); // category -> Set(subcategories)
  (Array.isArray(trees) ? trees : []).forEach((node) => {
    const category = String(node?.category || "").trim();
    if (!category) return;
    if (!map.has(category)) map.set(category, new Set());
    const set = map.get(category);
    (Array.isArray(node?.subcategories) ? node.subcategories : []).forEach((sc) => {
      const v = String(sc || "").trim();
      if (v) set.add(v);
    });
  });
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, subSet]) => ({
      category,
      subcategories: Array.from(subSet).sort((a, b) => a.localeCompare(b)),
    }));
}

function extractTotalCount(data, catalogLike) {
  if (typeof catalogLike?.total_count === "number") return catalogLike.total_count;
  if (typeof data?.total_count === "number") return data.total_count;
  if (Array.isArray(data)) {
    const firstWithTotal = data.find((x) => typeof x?.total_count === "number");
    if (typeof firstWithTotal?.total_count === "number") return firstWithTotal.total_count;
  }
  return null;
}

export const fetchActowizSearch = createAsyncThunk(
  "actowiz/fetchSearch",
  async (
    { query, query_list, platform_list, category, sub_category, start = 0, offset = 10, limit, mode = "replace" } = {},
    thunkApi
  ) => {
    const pageLimit = typeof limit === "number" ? limit : offset;
    const qs = new URLSearchParams();
    const queryListValue =
      query_list != null && String(query_list).trim()
        ? String(query_list)
        : String(query || "");
    const queryKey = String(queryListValue || "");
    qs.set("query_list", queryListValue);
    const platformListParam = formatPlatformListForQuery(platform_list);
    if (platformListParam) qs.set("platform_list", platformListParam);
    appendListQueryParam(qs, "category", category);
    appendListQueryParam(qs, "sub_category", sub_category);
    qs.set("start", String(start ?? 0));
    qs.set("offset", String(offset ?? 10));

    const res = await apiRequest(`api/actowiz/products?${qs.toString()}`, {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to load search results."
      );
    }

    const data = res.data;
    const catalogLike =
      Array.isArray(data) &&
      data.length === 1 &&
      data[0] &&
      typeof data[0] === "object" &&
      Array.isArray(data[0]?.products)
        ? data[0]
        : data && typeof data === "object" && Array.isArray(data?.products)
        ? data
        : null;

    const total =
      typeof catalogLike?.total_count === "number"
        ? catalogLike.total_count
        : typeof data?.total_count === "number"
        ? data.total_count
        : null;

    const categories = (() => {
      if (Array.isArray(catalogLike?.categories) && catalogLike.categories.length > 0) {
        return catalogLike.categories;
      }
      if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0]?.categories)) {
        const all = data.flatMap((x) => (Array.isArray(x?.categories) ? x.categories : []));
        const merged = mergeCategoryTrees(all);
        return merged.length ? merged : null;
      }
      if (Array.isArray(data?.categories) && data.categories.length > 0) {
        return data.categories;
      }
      return null;
    })();

    const rawProducts = Array.isArray(catalogLike?.products)
      ? catalogLike.products
      : Array.isArray(data?.products)
      ? data.products
      : Array.isArray(data)
      ? data
      : [];

    const items = rawProducts
      .map((p) => normalizeProvider(p))
      .filter((x) => x && x.title)
      .slice(0, pageLimit);

    return {
      query,
      queryKey,
      start,
      offset,
      mode,
      limit: pageLimit,
      total,
      categories,
      items,
    };
  },
  {
    condition: (
      { query, query_list, start = 0, offset = 10, limit, force } = {},
      { getState }
    ) => {
      const q = query_list != null && String(query_list).trim() ? String(query_list) : String(query || "");
      if (!String(q || "").trim()) return false;
      if (force) return true;
      const pageLimit = typeof limit === "number" ? limit : offset;
      const key = getSearchRequestKey(q, start, offset, pageLimit);
      const s = getState()?.actowiz?.requests?.[key];
      if (s === "loading" || s === "succeeded") return false;
      return true;
    },
  }
);

export const fetchActowizProducts = createAsyncThunk(
  "actowiz/fetchProducts",
  async (
    {
      query_list,
      platform,
      platform_list,
      category,
      sub_category,
      start = 0,
      offset = 10,
      mode = "append",
      endpoint = "products",
    } = {},
    thunkApi
  ) => {
    const normalizeListParam = (value) => {
      if (Array.isArray(value)) {
        return value
          .map((x) => String(x || "").trim())
          .filter(Boolean)
          .join(",");
      }
      return String(value || "").trim();
    };
    const normalizedPlatform = normalizePlatformKey(platform);
    const platformQuery = toActowizPlatformQueryParam(platform);
    const qs = new URLSearchParams();
    const platformList = formatPlatformListForQuery(platform_list);

    // New API expects `platform_list`, but keep backward compat with `platform`.
    if (platformList) qs.set("platform_list", platformList);
    else if (platformQuery) qs.set("platform", platformQuery);

    const categoryParam = normalizeListParam(category);
    const subCategoryParam = normalizeListParam(sub_category);
    const queryListParam = normalizeListParam(query_list);
    if (queryListParam) qs.set("query_list", queryListParam);
    appendListQueryParam(qs, "category", category);
    appendListQueryParam(qs, "sub_category", sub_category);
    qs.set("start", String(start ?? 0));
    qs.set("offset", String(offset ?? 10));
    const targetEndpoint = endpoint === "top_deals" ? "top_deals" : "products";
    const res = await apiRequest(`api/actowiz/${targetEndpoint}?${qs.toString()}`, {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to load products."
      );
    }

    // Actowiz payloads come in multiple shapes depending on endpoint/version:
    // - { results: [ { providers: [...] }, ... ] }
    // - { products: [ <provider-like product>, ... ] }
    // - [ { platform, results: [ <provider-like product>, ... ] }, ... ]
    // - [ { platform, products: [ <provider-like product>, ... ] }, ... ]
    // - [ <provider-like product>, ... ]
    // - [ { total_count, page, per_page, platform, categories: [...], products: [...] } ]  (admin v2)
    const data = res.data;
    const catalogLike =
      Array.isArray(data) &&
      data.length === 1 &&
      data[0] &&
      typeof data[0] === "object" &&
      Array.isArray(data[0]?.products) &&
      Array.isArray(data[0]?.categories)
        ? data[0]
        : data && typeof data === "object" && Array.isArray(data?.products) && Array.isArray(data?.categories)
        ? data
        : null;

    const totalCount = extractTotalCount(data, catalogLike);

    const categories = (() => {
      if (Array.isArray(catalogLike?.categories) && catalogLike.categories.length > 0) {
        return catalogLike.categories;
      }
      if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0]?.categories)) {
        const all = data.flatMap((x) => (Array.isArray(x?.categories) ? x.categories : []));
        const merged = mergeCategoryTrees(all);
        return merged.length ? merged : null;
      }
      return null;
    })();

    const results = Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data?.products)
      ? data.products
      : Array.isArray(data)
      ? data
      : [];

    const providers = (() => {
      // Shape: [ { platform, results: [...] }, ... ]
      if (Array.isArray(results) && results.length && Array.isArray(results[0]?.results)) {
        return results.flatMap((group) => (Array.isArray(group?.results) ? group.results : []));
      }
      // Shape: [ { platform, products: [...] }, ... ]
      if (Array.isArray(results) && results.length && Array.isArray(results[0]?.products)) {
        return results.flatMap((group) => (Array.isArray(group?.products) ? group.products : []));
      }
      // Shape: [ { providers: [...] }, ... ]
      if (Array.isArray(results) && results.length && Array.isArray(results[0]?.providers)) {
        return results.flatMap((r) => (Array.isArray(r?.providers) ? r.providers : []));
      }
      // Shape: [ { total_count, categories, products }, ... ] (admin v2)
      if (Array.isArray(results) && results.length && Array.isArray(results[0]?.products)) {
        return results.flatMap((r) => (Array.isArray(r?.products) ? r.products : []));
      }
      // Shape: [ <provider-like product>, ... ]
      return results;
    })();

    const batchLastUpdated =
      Array.isArray(data) &&
      data.length > 0 &&
      typeof data[0]?.last_updated_time === "number"
        ? data[0].last_updated_time
        : null;

    const normalizedItems = providers.map((pr) =>
      normalizeProvider(
        batchLastUpdated != null ? { ...pr, last_updated_time: batchLastUpdated } : pr
      )
    );

    return {
      query_list: queryListParam,
      platform: normalizedPlatform,
      endpoint: targetEndpoint,
      start,
      offset,
      mode,
      totalCount,
      categories,
      fetchedCount: providers.length,
      items: normalizedItems.filter((x) => x.title),
    };
  },
  {
    condition: (
      {
        query_list,
        platform,
        platform_list,
        category,
        sub_category,
        start = 0,
        offset = 10,
        force,
        endpoint = "products",
      } = {},
      { getState }
    ) => {
      if (force) return true;
      const queryKey = String(query_list || "").trim().toLowerCase();
      const endpointKey = endpoint === "top_deals" ? "top_deals" : "products";
      const normalizedPlatform = normalizePlatformKey(platform);
      const platListKey = Array.isArray(platform_list)
        ? platform_list.join(",")
        : String(platform_list || "");
      const catKey = String(category || "").trim().toLowerCase();
      const subKey = String(sub_category || "").trim().toLowerCase();
      const key = `${endpointKey}:${encodeURIComponent(queryKey)}:${normalizedPlatform || platListKey || "all"}:${encodeURIComponent(
        catKey
      )}:${encodeURIComponent(subKey)}:${start}:${offset}`;
      const s = getState()?.actowiz?.requests?.[key];
      if (s === "loading" || s === "succeeded") return false;
      return true;
    },
  }
);

export const fetchActowizFeatures = createAsyncThunk(
  "actowiz/fetchFeatures",
  async (
    { query_list, category, sub_category, offer, start = 0, offset = 10, mode = "append" } = {},
    thunkApi
  ) => {
    const normalizeListParam = (value) => {
      if (Array.isArray(value)) {
        return value
          .map((x) => String(x || "").trim())
          .filter(Boolean)
          .join(",");
      }
      return String(value || "").trim();
    };

    const queryList = String(query_list || "").trim();
    const categoryParam = normalizeListParam(category);
    const subCategoryParam = normalizeListParam(sub_category);
    const qs = new URLSearchParams();
    if (queryList) qs.set("query_list", queryList);
    appendListQueryParam(qs, "category", category);
    appendListQueryParam(qs, "sub_category", sub_category);
    if (offer) qs.set("offer", "true");
    qs.set("start", String(start ?? 0));
    qs.set("offset", String(offset ?? 10));
    const res = await apiRequest(`api/actowiz/features?${qs.toString()}`, {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to load, No Result Found."
      );
    }

    const payload = res.data;
    const catalogLike =
      Array.isArray(payload) &&
      payload.length === 1 &&
      payload[0] &&
      typeof payload[0] === "object" &&
      Array.isArray(payload[0]?.products)
        ? payload[0]
        : payload && typeof payload === "object" && Array.isArray(payload?.products)
        ? payload
        : null;

    const categories = (() => {
      if (Array.isArray(catalogLike?.categories) && catalogLike.categories.length > 0) {
        return catalogLike.categories;
      }
      if (Array.isArray(payload) && payload.length > 0 && Array.isArray(payload[0]?.categories)) {
        const all = payload.flatMap((x) => (Array.isArray(x?.categories) ? x.categories : []));
        const merged = mergeCategoryTrees(all);
        return merged.length ? merged : null;
      }
      if (Array.isArray(payload?.categories) && payload.categories.length > 0) {
        return payload.categories;
      }
      return null;
    })();

    const results = Array.isArray(payload?.results)
      ? payload.results
      : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(catalogLike?.products)
      ? catalogLike.products
      : Array.isArray(payload)
      ? payload
      : [];
    const totalCount = extractTotalCount(payload, catalogLike);

    // Important: features `offset` is based on `results` records (SKUs),
    // but each result can have many `providers` (one per retailer).
    // UI should render 1 card per `result`, otherwise card count != pagination.
    const items = results
      .map((r) => {
        const providers = Array.isArray(r?.providers)
          ? r.providers
          : r && typeof r === "object"
          ? [r]
          : [];
        const normalizedProviders = providers.map((provider) =>
          normalizeProvider({
            ...provider,
            sku: provider?.sku ?? r?.sku ?? provider?.product_id,
          })
        );
        const best = pickBestProvider(normalizedProviders);
        if (!best) return null;
        return {
          ...best,
          comparison: buildComparison(normalizedProviders),
        };
      })
      .filter((x) => x && x.title);

    return {
      query_list: queryList,
      category: categoryParam,
      sub_category: subCategoryParam,
      offer: Boolean(offer),
      start,
      offset,
      mode,
      totalCount,
      categories,
      fetchedCount: results.length,
      basketTotals: buildBasketTotalsFromApiPrices(payload),
      items,
    };
  },
  {
    condition: ({ query_list, category, sub_category, offer, start = 0, offset = 10, force } = {}, { getState }) => {
      if (force) return true;
      const key = getFeaturesRequestKey({ query_list, category, sub_category, offer, start, offset });
      const s = getState()?.actowiz?.requests?.[key];
      if (s === "loading" || s === "succeeded") return false;
      return true;
    },
  }
);

export const fetchActowizTopDeals = createAsyncThunk(
  "actowiz/fetchTopDeals",
  async (
    { query_list, platform_list, category, sub_category, category_list, start = 0, offset = 10, mode = "append" } = {},
    thunkApi
  ) => {
    const normalizeListParam = (value) => {
      if (Array.isArray(value)) {
        return value
          .map((x) => String(x || "").trim())
          .filter(Boolean)
          .join(",");
      }
      return String(value || "").trim();
    };

    const normalizedPlatformList = formatPlatformListForQuery(platform_list);

    const queryListParam = normalizeListParam(query_list);
    const categoryParam = normalizeListParam(category);
    const subCategoryParam = normalizeListParam(sub_category);
    const categoryListParam = normalizeListParam(category_list);

    const qs = new URLSearchParams({
      start: String(start ?? 0),
      offset: String(offset ?? 10),
    });
    if (queryListParam) qs.set("query_list", queryListParam);
    if (normalizedPlatformList) qs.set("platform_list", normalizedPlatformList);
    appendListQueryParam(qs, "category", category);
    appendListQueryParam(qs, "sub_category", sub_category);
    appendListQueryParam(qs, "category_list", category_list);

    const res = await apiRequest(`api/actowiz/top_deals?${qs.toString()}`, {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to load top deals."
      );
    }

    // Actowiz payloads come in multiple shapes:
    // - Legacy: [ { category, providers: [...] }, ... ]
    // - Admin v2: [ { total_count, page, per_page, categories: [...], products: [...] } ]
    // - Admin v2 (object): { total_count, page, per_page, categories: [...], products: [...] }
    const data = res.data;
    const catalogLike =
      Array.isArray(data) &&
      data.length === 1 &&
      data[0] &&
      typeof data[0] === "object" &&
      Array.isArray(data[0]?.products)
        ? data[0]
        : data && typeof data === "object" && Array.isArray(data?.products)
        ? data
        : null;

    const totalCount = extractTotalCount(data, catalogLike);

    const categories = (() => {
      if (Array.isArray(catalogLike?.categories) && catalogLike.categories.length > 0) {
        return catalogLike.categories;
      }
      if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0]?.categories)) {
        const all = data.flatMap((x) => (Array.isArray(x?.categories) ? x.categories : []));
        const merged = mergeCategoryTrees(all);
        return merged.length ? merged : null;
      }
      return null;
    })();

    const providers = (() => {
      if (Array.isArray(catalogLike?.products)) return catalogLike.products;
      const categories = Array.isArray(data) ? data : [];
      return categories.flatMap((c) => (Array.isArray(c?.providers) ? c.providers : []));
    })();

    const groupedBySku = new Map();
    providers.forEach((provider) => {
      const normalized = normalizeProvider(provider);
      const key = normalized.pid || normalized.sku || "";
      if (!key || !normalized.title) return;
      if (!groupedBySku.has(key)) groupedBySku.set(key, []);
      groupedBySku.get(key).push(normalized);
    });

    const items = Array.from(groupedBySku.values())
      .map((group) => {
        const best = pickBestProvider(group);
        if (!best) return null;
        return {
          ...best,
          comparison: buildComparison(group),
        };
      })
      .filter((x) => x && x.title);

    return {
      query_list: queryListParam,
      platform_list: normalizedPlatformList,
      category: categoryParam,
      sub_category: subCategoryParam,
      category_list: categoryListParam,
      start,
      offset,
      mode,
      totalCount,
      categories,
      fetchedCount: providers.length,
      items,
    };
  },
  {
    condition: (
      { query_list, platform_list, category, sub_category, category_list, start = 0, offset = 10, force } = {},
      { getState }
    ) => {
      if (force) return true;
      const queryKey = String(query_list || "").trim().toLowerCase();
      const platKey = Array.isArray(platform_list)
        ? platform_list.join(",")
        : String(platform_list || "");
      const catKey = String(category || "").trim().toLowerCase();
      const subKey = String(sub_category || "").trim().toLowerCase();
      const categoryListKey = String(category_list || "").trim().toLowerCase();
      const key = `top_deals:${encodeURIComponent(queryKey)}:${encodeURIComponent(platKey)}:${encodeURIComponent(
        catKey
      )}:${encodeURIComponent(subKey)}:${encodeURIComponent(categoryListKey)}:${start}:${offset}`;
      const s = getState()?.actowiz?.requests?.[key];
      if (s === "loading" || s === "succeeded") return false;
      return true;
    },
  }
);

const initialState = {
  byPlatform: {
    tesco: [],
    asda: [],
    morrison: [],
    sainsbury: [],
  },
  byPlatformLastBatchSize: {
    tesco: 0,
    asda: 0,
    morrison: 0,
    sainsbury: 0,
  },
  byPlatformFetchedTotal: {
    tesco: 0,
    asda: 0,
    morrison: 0,
    sainsbury: 0,
  },
  platformCatalog: {
    // platform -> { totalCount: number|null, categories: Array<{category: string, subcategories: string[]}>|null }
  },
  allCatalog: {
    totalCount: null,
    categories: null,
  },
  /** Filter tree for the active `/search` query; not overwritten by product-detail fetches. */
  searchCatalog: {
    queryKey: null,
    totalCount: null,
    categories: null,
  },
  /** Filter tree for `/top-deals`; kept separate so search filters do not leak in. */
  topDealsCatalog: {
    queryKey: null,
    totalCount: null,
    categories: null,
  },
  /** Flat list from `GET actowiz/products` with no `platform` query (used by Quick Compare “Base”). */
  unfilteredProducts: [],
  /** Raw batch size from the last unfiltered products API response. */
  productsLastBatchSize: 0,
  /** Cumulative API result count for the next unfiltered products request. */
  productsFetchedTotal: 0,
  features: [],
  featuresTotal: null,
  /** Raw `results.length` from the last features API response (used for pagination). */
  featuresLastBatchSize: 0,
  /** Cumulative API result count (`start + lastBatch`) for the next request. */
  featuresFetchedTotal: 0,
  featuresBasketTotals: null,
  featuresStatus: "idle", // idle | loading | succeeded | failed
  featuresError: "",
  topDeals: [],
  topDealsTotal: null,
  /** Raw batch size from the last top deals API response. */
  topDealsLastBatchSize: 0,
  /** Cumulative API result count for the next top deals request. */
  topDealsFetchedTotal: 0,
  topDealsStatus: "idle", // idle | loading | succeeded | failed
  topDealsError: "",
  search: [],
  searchStatus: "idle", // idle | loading | succeeded | failed
  searchError: "",
  searchTotal: null,
  statusByPlatform: {
    tesco: "idle",
    asda: "idle",
    morrison: "idle",
    sainsbury: "idle",
  },
  errorByPlatform: {
    tesco: "",
    asda: "",
    morrison: "",
    sainsbury: "",
  },
  // track pagination requests to avoid duplicate calls
  requests: {},
};

const actowizSlice = createSlice({
  name: "actowiz",
  initialState,
  reducers: {
    clearActowiz(state) {
      state.byPlatform = { tesco: [], asda: [], morrison: [], sainsbury: [] };
      state.byPlatformLastBatchSize = { tesco: 0, asda: 0, morrison: 0, sainsbury: 0 };
      state.byPlatformFetchedTotal = { tesco: 0, asda: 0, morrison: 0, sainsbury: 0 };
      state.platformCatalog = {};
      state.allCatalog = { totalCount: null, categories: null };
      state.searchCatalog = { queryKey: null, totalCount: null, categories: null };
      state.topDealsCatalog = { queryKey: null, totalCount: null, categories: null };
      state.unfilteredProducts = [];
      state.productsLastBatchSize = 0;
      state.productsFetchedTotal = 0;
      state.features = [];
      state.featuresTotal = null;
      state.featuresLastBatchSize = 0;
      state.featuresFetchedTotal = 0;
      state.featuresBasketTotals = null;
      state.featuresStatus = "idle";
      state.featuresError = "";
      state.topDeals = [];
      state.topDealsTotal = null;
      state.topDealsLastBatchSize = 0;
      state.topDealsFetchedTotal = 0;
      state.topDealsStatus = "idle";
      state.topDealsError = "";
      state.search = [];
      state.searchStatus = "idle";
      state.searchError = "";
      state.searchTotal = null;
      state.statusByPlatform = {
        tesco: "idle",
        asda: "idle",
        morrison: "idle",
        sainsbury: "idle",
      };
      state.errorByPlatform = { tesco: "", asda: "", morrison: "", sainsbury: "" };
      state.requests = {};
    },
    clearActowizFeatures(state) {
      state.features = [];
      state.featuresTotal = null;
      state.featuresStatus = "idle";
      state.featuresError = "";
      // Clear only features pagination request markers
      Object.keys(state.requests).forEach((k) => {
        if (k.startsWith("features_v2:")) delete state.requests[k];
      });
    },
    clearActowizRequest(state, action) {
      const key = String(action.payload || "");
      if (key) delete state.requests[key];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActowizProducts.pending, (state, action) => {
        const {
          query_list,
          platform,
          platform_list,
          category,
          sub_category,
          start = 0,
          offset = 10,
          endpoint = "products",
          mode,
        } =
          action.meta.arg || {};
        const isFreshPage = start === 0;
        const queryKey = String(query_list || "").trim().toLowerCase();
        const endpointKey = endpoint === "top_deals" ? "top_deals" : "products";
        const normalizedPlatform = normalizePlatformKey(platform);
        const platListKey = Array.isArray(platform_list)
          ? platform_list.join(",")
          : String(platform_list || "");
        const catKey = String(category || "").trim().toLowerCase();
        const subKey = String(sub_category || "").trim().toLowerCase();
        const key = `${endpointKey}:${encodeURIComponent(queryKey)}:${normalizedPlatform || platListKey || "all"}:${encodeURIComponent(
          catKey
        )}:${encodeURIComponent(subKey)}:${start}:${offset}`;
        state.requests[key] = "loading";
        if (normalizedPlatform) {
          state.statusByPlatform[normalizedPlatform] = "loading";
          state.errorByPlatform[normalizedPlatform] = "";
          if (mode === "replace" && isFreshPage) {
            state.byPlatform[normalizedPlatform] = [];
            state.byPlatformLastBatchSize[normalizedPlatform] = 0;
            state.byPlatformFetchedTotal[normalizedPlatform] = 0;
          }
        } else {
          Object.keys(state.statusByPlatform).forEach((k) => {
            state.statusByPlatform[k] = "loading";
            state.errorByPlatform[k] = "";
          });
          if (mode === "replace" && isFreshPage) {
            state.unfilteredProducts = [];
            state.productsLastBatchSize = 0;
            state.productsFetchedTotal = 0;
          }
        }
      })
      .addCase(fetchActowizProducts.fulfilled, (state, action) => {
        const {
          query_list,
          platform,
          endpoint = "products",
          start = 0,
          offset = 10,
          items,
          mode,
          totalCount,
          categories,
          fetchedCount,
        } =
          action.payload || {};
        const {
          query_list: queryListArg,
          platform_list: platformListArg,
          category: categoryArg,
          sub_category: subCategoryArg,
        } = action.meta.arg || {};
        const queryKey = String(query_list || queryListArg || "").trim().toLowerCase();
        const endpointKey = endpoint === "top_deals" ? "top_deals" : "products";
        const platListKey = Array.isArray(platformListArg)
          ? platformListArg.join(",")
          : String(platformListArg || "");
        const catKey = String(categoryArg || "").trim().toLowerCase();
        const subKey = String(subCategoryArg || "").trim().toLowerCase();
        const key = `${endpointKey}:${encodeURIComponent(queryKey)}:${platform || platListKey || "all"}:${encodeURIComponent(
          catKey
        )}:${encodeURIComponent(subKey)}:${start}:${offset}`;
        state.requests[key] = "succeeded";

        const catArgStr = Array.isArray(categoryArg) ? categoryArg.join(",") : String(categoryArg || "");
        const subArgStr = Array.isArray(subCategoryArg) ? subCategoryArg.join(",") : String(subCategoryArg || "");
        const hasFilters = Boolean(catArgStr.trim() || subArgStr.trim());

        if (platform) {
          state.statusByPlatform[platform] = "succeeded";
          state.errorByPlatform[platform] = "";

          // Important:
          // Filtered product calls return a *narrowed* `categories` list (only selected categories),
          // so we should NOT overwrite the master category tree used for the Filters UI.
          // Only refresh the master catalog on unfiltered calls (or if we don't have it yet).
          const existing = state.platformCatalog?.[platform] || null;
          const canUpdateCatalog =
            !hasFilters || !existing || !Array.isArray(existing?.categories) || existing.categories.length === 0;

          if (canUpdateCatalog && (totalCount != null || categories != null)) {
            state.platformCatalog[platform] = {
              totalCount:
                typeof totalCount === "number"
                  ? totalCount
                  : existing?.totalCount ?? null,
              categories:
                Array.isArray(categories) && categories.length > 0
                  ? categories
                  : existing?.categories ?? null,
            };
          } else if (totalCount != null && existing) {
            // Always keep totalCount in sync with latest API response, including filtered calls.
            existing.totalCount = typeof totalCount === "number" ? totalCount : existing.totalCount ?? null;
          }
          if (mode === "replace") {
            state.byPlatform[platform] = items || [];
          } else {
            // Append mode
            if (start === 0) state.byPlatform[platform] = items || [];
            else state.byPlatform[platform] = [...(state.byPlatform[platform] || []), ...(items || [])];
          }
          state.byPlatformLastBatchSize[platform] =
            typeof fetchedCount === "number" ? fetchedCount : Array.isArray(items) ? items.length : 0;
          state.byPlatformFetchedTotal[platform] = (start ?? 0) + state.byPlatformLastBatchSize[platform];
        } else {
          const existing = state.allCatalog || null;
          const canUpdateCatalog =
            !hasFilters || !existing || !Array.isArray(existing?.categories) || existing.categories.length === 0;

          if (canUpdateCatalog && (totalCount != null || categories != null)) {
            state.allCatalog = {
              totalCount: typeof totalCount === "number" ? totalCount : existing?.totalCount ?? null,
              categories: Array.isArray(categories) && categories.length > 0 ? categories : existing?.categories ?? null,
            };
          } else if (totalCount != null && existing) {
            // Always keep totalCount in sync with latest API response, including filtered calls.
            existing.totalCount = typeof totalCount === "number" ? totalCount : existing.totalCount ?? null;
          }
          if (mode === "replace") {
            state.unfilteredProducts = items || [];
          } else if (start === 0) {
            state.unfilteredProducts = items || [];
          } else {
            state.unfilteredProducts = [...(state.unfilteredProducts || []), ...(items || [])];
          }
          state.productsLastBatchSize =
            typeof fetchedCount === "number" ? fetchedCount : Array.isArray(items) ? items.length : 0;
          state.productsFetchedTotal = (start ?? 0) + state.productsLastBatchSize;
          const grouped = { tesco: [], asda: [], morrison: [], sainsbury: [] };
          (items || []).forEach((item) => {
            const p = normalizePlatformKey(item?.platform);
            if (p) grouped[p].push(item);
          });
          Object.keys(grouped).forEach((k) => {
            state.statusByPlatform[k] = "succeeded";
            state.errorByPlatform[k] = "";
            if (mode === "replace") state.byPlatform[k] = grouped[k];
            else if (start === 0) state.byPlatform[k] = grouped[k];
            else state.byPlatform[k] = [...(state.byPlatform[k] || []), ...grouped[k]];
          });
        }
      })
      .addCase(fetchActowizProducts.rejected, (state, action) => {
        const {
          query_list,
          platform,
          platform_list,
          category,
          sub_category,
          start = 0,
          offset = 10,
          endpoint = "products",
        } =
          action.meta.arg || {};
        const queryKey = String(query_list || "").trim().toLowerCase();
        const endpointKey = endpoint === "top_deals" ? "top_deals" : "products";
        const normalizedPlatform = normalizePlatformKey(platform);
        const platListKey = Array.isArray(platform_list)
          ? platform_list.join(",")
          : String(platform_list || "");
        const catKey = String(category || "").trim().toLowerCase();
        const subKey = String(sub_category || "").trim().toLowerCase();
        const key = `${endpointKey}:${encodeURIComponent(queryKey)}:${normalizedPlatform || platListKey || "all"}:${encodeURIComponent(
          catKey
        )}:${encodeURIComponent(subKey)}:${start}:${offset}`;
        state.requests[key] = "failed";
        if (normalizedPlatform) {
          state.statusByPlatform[normalizedPlatform] = "failed";
          state.errorByPlatform[normalizedPlatform] =
            typeof action.payload === "string" ? action.payload : "Failed to load products.";
        } else {
          Object.keys(state.statusByPlatform).forEach((k) => {
            state.statusByPlatform[k] = "failed";
            state.errorByPlatform[k] =
              typeof action.payload === "string" ? action.payload : "Failed to load products.";
          });
        }
      })
      .addCase(fetchActowizFeatures.pending, (state, action) => {
        const { query_list, category, sub_category, offer, start = 0, offset = 10, mode } = action.meta.arg || {};
        const isFreshPage = start === 0;
        const key = getFeaturesRequestKey({ query_list, category, sub_category, offer, start, offset });
        state.requests[key] = "loading";
        state.featuresStatus = "loading";
        state.featuresError = "";
        if (mode === "replace" && isFreshPage) {
          state.features = [];
          state.featuresTotal = null;
          state.featuresLastBatchSize = 0;
          state.featuresFetchedTotal = 0;
          state.featuresBasketTotals = null;
        }
      })
      .addCase(fetchActowizFeatures.fulfilled, (state, action) => {
        const {
          query_list,
          category,
          sub_category,
          offer,
          start = 0,
          offset = 10,
          items,
          mode,
          totalCount,
          categories,
          fetchedCount,
          basketTotals,
        } = action.payload || {};
        const key = getFeaturesRequestKey({ query_list, category, sub_category, offer, start, offset });
        state.requests[key] = "succeeded";
        state.featuresStatus = "succeeded";
        state.featuresError = "";
        state.featuresLastBatchSize =
          typeof fetchedCount === "number" ? fetchedCount : Array.isArray(items) ? items.length : 0;
        state.featuresFetchedTotal = (start ?? 0) + state.featuresLastBatchSize;
        if (typeof totalCount === "number") {
          state.featuresTotal = totalCount;
        } else if (mode === "replace" && (items?.length ?? 0) === 0) {
          state.featuresTotal = 0;
        }

        const hasFilters = Boolean(
          hasListFilterValue(category) || hasListFilterValue(sub_category)
        );

        if (Array.isArray(categories) && categories.length > 0) {
          const queryListKey = String(query_list || "").trim().toLowerCase();
          applySearchCatalogUpdate(state, {
            queryKey: queryListKey,
            totalCount,
            categories,
            hasFilters,
          });

          const existing = state.allCatalog || { totalCount: null, categories: null };
          const canUpdateCatalog = canUpdateSharedAllCatalog(existing, { hasFilters, queryListKey });

          if (canUpdateCatalog) {
            state.allCatalog = {
              totalCount:
                typeof totalCount === "number" ? totalCount : existing?.totalCount ?? null,
              categories,
            };
          } else if (typeof totalCount === "number" && existing) {
            existing.totalCount = totalCount;
          }
        }

        if (mode === "replace") state.features = items || [];
        else {
          if (start === 0) state.features = items || [];
          else state.features = [...(state.features || []), ...(items || [])];
        }
        if (basketTotals?.cheapest) {
          state.featuresBasketTotals = basketTotals;
        }
      })
      .addCase(fetchActowizFeatures.rejected, (state, action) => {
        const { query_list, category, sub_category, offer, start = 0, offset = 10, mode } =
          action.meta.arg || {};
        const key = getFeaturesRequestKey({ query_list, category, sub_category, offer, start, offset });
        state.requests[key] = "failed";
        state.featuresStatus = "failed";
        state.featuresError =
          typeof action.payload === "string" ? action.payload : "Failed to Load, No Result Found.";
        if (mode === "replace" && (action.meta.arg?.start ?? 0) === 0) {
          state.features = [];
          state.featuresTotal = 0;
          state.featuresLastBatchSize = 0;
          state.featuresFetchedTotal = 0;
          state.featuresBasketTotals = null;
        }
      });

    builder
      .addCase(fetchActowizTopDeals.pending, (state, action) => {
        const { query_list, platform_list, category, sub_category, category_list, start = 0, offset = 10, mode } =
          action.meta.arg || {};
        const isFreshPage = start === 0;
        const queryKey = String(query_list || "").trim().toLowerCase();
        const platKey = Array.isArray(platform_list)
          ? platform_list.join(",")
          : String(platform_list || "");
        const catKey = String(category || "").trim().toLowerCase();
        const subKey = String(sub_category || "").trim().toLowerCase();
        const categoryListKey = String(category_list || "").trim().toLowerCase();
        const catArgStr = Array.isArray(category)
          ? category.join(",")
          : String(category || "").trim();
        const subArgStr = Array.isArray(sub_category)
          ? sub_category.join(",")
          : String(sub_category || "").trim();
        const categoryListArgStr = Array.isArray(category_list)
          ? category_list.join(",")
          : String(category_list || "").trim();
        const hasFilters = Boolean(catArgStr || subArgStr || categoryListArgStr);
        const key = `top_deals:${encodeURIComponent(queryKey)}:${encodeURIComponent(platKey)}:${encodeURIComponent(
          catKey
        )}:${encodeURIComponent(subKey)}:${encodeURIComponent(categoryListKey)}:${start}:${offset}`;
        state.requests[key] = "loading";
        state.topDealsStatus = "loading";
        state.topDealsError = "";
        if (mode === "replace" && isFreshPage) {
          state.topDeals = [];
          state.topDealsTotal = null;
          state.topDealsLastBatchSize = 0;
          state.topDealsFetchedTotal = 0;
          if (!hasFilters) {
            state.topDealsCatalog = { queryKey: null, totalCount: null, categories: null };
          }
        }
      })
      .addCase(fetchActowizTopDeals.fulfilled, (state, action) => {
        const {
          query_list,
          platform_list,
          category,
          sub_category,
          category_list,
          start = 0,
          offset = 10,
        } = action.meta.arg || {};
        const { items, mode, totalCount, categories, fetchedCount } = action.payload || {};
        const catArgStr = Array.isArray(category)
          ? category.join(",")
          : String(category || "").trim();
        const subArgStr = Array.isArray(sub_category)
          ? sub_category.join(",")
          : String(sub_category || "").trim();
        const categoryListArgStr = Array.isArray(category_list)
          ? category_list.join(",")
          : String(category_list || "").trim();
        const hasFilters = Boolean(catArgStr || subArgStr || categoryListArgStr);
        const queryKey = String(query_list || "").trim().toLowerCase();
        const platKey = Array.isArray(platform_list)
          ? platform_list.join(",")
          : String(platform_list || "");
        const catKey = String(category || "").trim().toLowerCase();
        const subKey = String(sub_category || "").trim().toLowerCase();
        const categoryListKey = String(category_list || "").trim().toLowerCase();
        const key = `top_deals:${encodeURIComponent(queryKey)}:${encodeURIComponent(platKey)}:${encodeURIComponent(
          catKey
        )}:${encodeURIComponent(subKey)}:${encodeURIComponent(categoryListKey)}:${start}:${offset}`;
        state.requests[key] = "succeeded";
        state.topDealsStatus = "succeeded";
        state.topDealsError = "";
        state.topDealsTotal = typeof totalCount === "number" ? totalCount : state.topDealsTotal;
        state.topDealsLastBatchSize =
          typeof fetchedCount === "number" ? fetchedCount : Array.isArray(items) ? items.length : 0;
        state.topDealsFetchedTotal = (start ?? 0) + state.topDealsLastBatchSize;

        if (
          (start ?? 0) === 0 &&
          !hasFilters &&
          Array.isArray(categories) &&
          categories.length > 0
        ) {
          state.topDealsCatalog = {
            queryKey,
            totalCount: typeof totalCount === "number" ? totalCount : state.topDealsCatalog?.totalCount ?? null,
            categories,
          };
        } else if ((start ?? 0) === 0 && !hasFilters && typeof totalCount === "number") {
          state.topDealsCatalog = {
            ...(state.topDealsCatalog || { queryKey: null, totalCount: null, categories: null }),
            queryKey,
            totalCount,
          };
        }

        if (Array.isArray(categories) && categories.length > 0) {
          const existing = state.allCatalog || { totalCount: null, categories: null };
          const canUpdateCatalog = canUpdateSharedAllCatalog(existing, {
            hasFilters,
            queryListKey: queryKey,
          });

          if (canUpdateCatalog) {
            state.allCatalog = {
              totalCount:
                typeof totalCount === "number" ? totalCount : existing?.totalCount ?? null,
              categories,
            };
          } else if (typeof totalCount === "number" && existing) {
            existing.totalCount = totalCount;
          }

          if (queryKey) {
            applySearchCatalogUpdate(state, {
              queryKey,
              totalCount,
              categories,
              hasFilters:
                hasFilters || Boolean(formatPlatformListForQuery(platform_list)),
            });
          }
        }
        if (mode === "replace") state.topDeals = items || [];
        else {
          if (start === 0) state.topDeals = items || [];
          else state.topDeals = [...(state.topDeals || []), ...(items || [])];
        }
      })
      .addCase(fetchActowizTopDeals.rejected, (state, action) => {
        const { query_list, platform_list, category, sub_category, category_list, start = 0, offset = 10 } =
          action.meta.arg || {};
        const queryKey = String(query_list || "").trim().toLowerCase();
        const platKey = Array.isArray(platform_list)
          ? platform_list.join(",")
          : String(platform_list || "");
        const catKey = String(category || "").trim().toLowerCase();
        const subKey = String(sub_category || "").trim().toLowerCase();
        const categoryListKey = String(category_list || "").trim().toLowerCase();
        const key = `top_deals:${encodeURIComponent(queryKey)}:${encodeURIComponent(platKey)}:${encodeURIComponent(
          catKey
        )}:${encodeURIComponent(subKey)}:${encodeURIComponent(categoryListKey)}:${start}:${offset}`;
        state.requests[key] = "failed";
        state.topDealsStatus = "failed";
        state.topDealsError =
          typeof action.payload === "string" ? action.payload : "Failed to load top deals.";
      });

    builder
      .addCase(fetchActowizSearch.pending, (state, action) => {
        const { query, query_list, start = 0, offset = 10, limit, mode } = action.meta.arg || {};
        const isFreshPage = start === 0;
        const qKey =
          query_list != null && String(query_list).trim() ? String(query_list) : String(query || "");
        const pageLimit = typeof limit === "number" ? limit : offset;
        const key = getSearchRequestKey(qKey, start, offset, pageLimit);
        state.requests[key] = "loading";
        state.searchStatus = "loading";
        state.searchError = "";
        if (mode === "replace" && isFreshPage) {
          state.search = [];
          state.searchTotal = null;
        }
      })
      .addCase(fetchActowizSearch.fulfilled, (state, action) => {
        const {
          start = 0,
          offset = 10,
          mode,
          items,
          query,
          queryKey,
          total,
          categories,
          limit: payloadLimit,
        } = action.payload || {};
        const { category, sub_category, platform_list } = action.meta.arg || {};
        const hasFilters = Boolean(
          hasListFilterValue(category) ||
            hasListFilterValue(sub_category) ||
            formatPlatformListForQuery(platform_list)
        );
        const pageLimit =
          typeof payloadLimit === "number" ? payloadLimit : offset;
        const key = getSearchRequestKey(queryKey != null ? queryKey : query, start, offset, pageLimit);
        state.requests[key] = "succeeded";
        state.searchStatus = "succeeded";
        state.searchError = "";
        state.searchTotal = typeof total === "number" ? total : state.searchTotal;

        const searchQueryKey = String(queryKey != null ? queryKey : query || "")
          .trim()
          .toLowerCase();
        if (Array.isArray(categories) && categories.length > 0) {
          applySearchCatalogUpdate(state, {
            queryKey: searchQueryKey,
            totalCount: total,
            categories,
            hasFilters,
          });

          const existing = state.allCatalog || { totalCount: null, categories: null };
          const canUpdateCatalog = canUpdateSharedAllCatalog(existing, {
            hasFilters,
            queryListKey: searchQueryKey,
          });

          if (canUpdateCatalog) {
            state.allCatalog = {
              totalCount:
                typeof total === "number" ? total : existing?.totalCount ?? null,
              categories,
            };
          } else if (typeof total === "number" && existing) {
            existing.totalCount = total;
          }
        }

        if (mode === "replace") state.search = items || [];
        else {
          if (start === 0) state.search = items || [];
          else state.search = [...(state.search || []), ...(items || [])];
        }
      })
      .addCase(fetchActowizSearch.rejected, (state, action) => {
        const { query, query_list, start = 0, offset = 10, limit } = action.meta.arg || {};
        const qKey =
          query_list != null && String(query_list).trim() ? String(query_list) : String(query || "");
        const pageLimit = typeof limit === "number" ? limit : offset;
        const key = getSearchRequestKey(qKey, start, offset, pageLimit);
        state.requests[key] = "failed";
        state.searchStatus = "failed";
        state.searchError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to load search results.";
      });
  },
});

export const { clearActowiz, clearActowizFeatures, clearActowizRequest } = actowizSlice.actions;
export default actowizSlice.reducer;

